/**
 * Pillar F.2 — the participant reveal. ONE renderer for "who was on this", used by the timeline's
 * expanded mail and calendar rows and by the message detail (page + modal).
 *
 * The linkability rule is the SERVER'S, mirrored here and nowhere else (internal-profile-visibility
 * §3.3, the same contract `EntityRefs.canOpenProfile` implements for entity refs):
 *
 *   link + hovercard   ⟺  the row is RESOLVED to a person AND `profile_hidden` is absent
 *   plain text         ⟺  unresolved (no local person), or `profile_hidden` (an Ember colleague)
 *
 * The front end never applies a domain test of its own — the payload carries no domain rule and it
 * must not be reinvented here (that is how a gate drifts). An internal name still RENDERS: a
 * colleague on a thread is a name, not a profile (§3.4). What it must never become is a link that
 * 404s, or a hovercard that probes an endpoint designed to deny it.
 *
 * `MailParticipants` exists so the query hook is MOUNTED with the reveal rather than called with an
 * `enabled` flag: a caller that is not showing the list has no hook at all, which is what makes
 * "never over-fetched" structural instead of a convention someone has to remember.
 *
 * ⛔ `MeetingParticipants` was DELETED by the timeline-inline build (2026-09-19,
 * `docs/specs/strategic-architecture/timeline-inline-message-spec.md` §3.2): the entity timeline's
 * meeting rows now carry every attendee, linked, on the COLLAPSED line off the page payload, so its
 * only caller went away. The governed `/meeting/{id}/attendees` door and `useCrmMeetingAttendees`
 * both STAY — cheap, still gated, and the record panel is expected to want them. Do not "clean up"
 * the hook: deleting a client for a live governed endpoint is not a simplification.
 *
 * `MailParticipants` itself now has one caller left, `CrmMessageDetail` (the message page + the
 * modal); it goes when the modal does.
 */
import { useCrmEmailParticipants } from '../../hooks/useCrm';
import type { CrmTimelineParticipant } from '../../types';
import { CrmPersonHovercard } from './CrmPersonHovercard';
import { profileHref } from './crmEntityRoles';
import styles from './crm.module.css';

/** The shape both mail participants and meeting attendees share for display purposes. */
export interface ParticipantLike {
  email_address: string | null;
  display_name: string | null;
  resolved_person_id: number | null;
  profile_hidden?: boolean;
}

export type OpenPerson = (id: number) => void;

function nameOf(p: ParticipantLike): string {
  return p.display_name || p.email_address || '(unknown)';
}

/** True when this participant's profile may be opened — see the module contract. */
function isLinkable(p: ParticipantLike): boolean {
  return p.resolved_person_id != null && p.profile_hidden !== true;
}

/** One participant: a hovercarded link, or plain text. Never a link that cannot resolve. */
export function ParticipantName({ p, openPerson }: { p: ParticipantLike; openPerson?: OpenPerson }) {
  const label = nameOf(p);
  const addr = p.email_address ?? undefined;
  const id = p.resolved_person_id;
  if (!isLinkable(p) || id == null) {
    return <span className={styles.tlPartyPlain} title={addr}>{label}</span>;
  }
  return (
    <CrmPersonHovercard personId={id}>
      {/* A real anchor so ⌘-click / middle-click / "open in new tab" behave; a plain left click is
          intercepted into the app's own selection navigation (never a hand-rolled pushState). */}
      <a
        className={styles.gridRefLink}
        href={profileHref('person', id)}
        title={addr}
        onClick={(e) => {
          if (openPerson == null || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          openPerson(id);
        }}
      >
        {label}
      </a>
    </CrmPersonHovercard>
  );
}

/** The timeline indents the reveal under the row's subject; the message detail sits in its own card
 *  and must not inherit that 94px offset. One prop rather than two components. */
const shell = (flush: boolean) => (flush ? `${styles.reveal} ${styles.revealFlush}` : styles.reveal);

function Group({ label, people, openPerson }: {
  label: string; people: ParticipantLike[]; openPerson?: OpenPerson;
}) {
  if (people.length === 0) return null;
  return (
    <div className={styles.revealRow}>
      <span className={styles.revealLabel}>{label}</span>
      <span className={styles.revealNames}>
        {people.map((p, i) => (
          <span key={`${p.resolved_person_id ?? p.email_address ?? i}-${i}`}>
            {i > 0 && <span className={styles.revealSep}>, </span>}
            <ParticipantName p={p} openPerson={openPerson} />
          </span>
        ))}
      </span>
    </div>
  );
}

const ROLES: Array<{ key: string; label: string }> = [
  { key: 'from', label: 'From' },
  { key: 'to', label: 'To' },
  { key: 'cc', label: 'Cc' },
  // bcc rows are server-redacted for everyone but the bcc'd principal and an audited all-access read
  // (governance.scoped_participants) — so if one arrives, it is meant for this caller.
  { key: 'bcc', label: 'Bcc' },
];
const KNOWN_ROLES = new Set(ROLES.map((r) => r.key));

/** Every participant on ONE message — fetched on mount (i.e. on expand / on modal open). */
export function MailParticipants({ emailId, openPerson, flush = false }: {
  emailId: number; openPerson?: OpenPerson; flush?: boolean;
}) {
  const q = useCrmEmailParticipants(emailId);
  const parts: CrmTimelineParticipant[] = q.data?.participants ?? [];
  // ⚠ `role` is nullable and not a closed enum on the wire, so banding on the four above DROPPED
  // anything else — a participant silently missing from the one list whose entire job is completeness
  // (review minor 6). Leftovers get their own band instead of being filtered away.
  const other = parts.filter((p) => p.role == null || !KNOWN_ROLES.has(p.role));
  return (
    <div className={shell(flush)}>
      {q.isLoading && <div className={styles.revealMuted}>Loading participants…</div>}
      {q.error != null && <div className={styles.revealMuted}>Participants unavailable.</div>}
      {!q.isLoading && q.error == null && parts.length === 0 && (
        <div className={styles.revealMuted}>No participants recorded.</div>
      )}
      {ROLES.map(({ key, label }) => (
        <Group key={key} label={label} openPerson={openPerson}
               people={parts.filter((p) => p.role === key)} />
      ))}
      <Group label="Other" people={other} openPerson={openPerson} />
    </div>
  );
}
